import React from "react";
import { TextLabel } from "../components/TextLabel";
import { TextInput, Button, showToast } from "@twioku/common_components";
import EmailImg from "../assets/email_default.svg";
import LineImg from "../assets/small_line_icon.svg";
import { useNavigate } from "react-router-dom";
import { Path } from "../routes/PagePath";
import { Controller } from "react-hook-form";
import { useNewAnswererInfo } from "../features/newAnswererInfo/hooks/useNewAnswererInfo";
import { useCreateNewAnswerer } from "../features/newAnswererInfo/hooks/useCreateNewAnswerer";
import { useLineLink } from "../features/lineLink/hooks/useLineLink";

export const AnswererRequiredInfoPage: React.FC = () => {
    const navigate = useNavigate();
    const { form, addAnswerer } = useNewAnswererInfo();
    const createNewAnswerer = useCreateNewAnswerer();
    const { createLineLink } = useLineLink();

    const {
        control,
        handleSubmit,
        watch,
        formState: { isValid },
    } = form;

    const watchedName = watch("name");
    const watchedRelationship = watch("relationship");
    const watchedQuestionSendType = watch("questionSendType");

    const onSubmit = async () => {
        if (watchedQuestionSendType === "Line") {
            try {
                // フォームデータを取得
                const formData = form.getValues();

                // 回答者を作成
                const result = await createNewAnswerer.mutateAsync({
                    ...formData,
                    questionSendType: "Line",
                });

                if (!result) {
                    showToast(
                        "回答者の作成に失敗しました。時間をおいて再度お試しください。",
                        "error"
                    );
                    return;
                }

                // answererIdとanswerersを更新（formの値は自動保存される）
                addAnswerer(result);

                // LINEリンクを生成（結果はcreateLineLink.dataに自動保存される）
                try {
                    await createLineLink.mutateAsync(result.answererId);
                } catch (e) {
                    showToast(
                        "リンクの生成に失敗しました。時間をおいて再度お試しください。",
                        "error"
                    );
                    return;
                }
                navigate(Path.answererLineLink);
            } catch (error) {
                showToast(
                    "回答者の作成に失敗しました。時間をおいて再度お試しください。",
                    "error"
                );
            }
        } else {
            navigate(Path.senderEmailConfirm);
        }
    };

    return (
        <div className="mx-6">
            <form onSubmit={handleSubmit(onSubmit)}>
                <h1 className="mb-3 mt-5 text-left text-xl font-bold">
                    どなたをご招待しますか？
                </h1>

                {/* 関係性選択 */}
                <Controller
                    name="relationship"
                    control={control}
                    render={({ field, fieldState }) => (
                        <div>
                            <div className="flex items-center space-x-3">
                                {(
                                    [
                                        { value: "father", label: "父" },
                                        { value: "mother", label: "母" },
                                        { value: "others", label: "その他" },
                                    ] as const
                                ).map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={`rounded-full border border-gray-300 px-4 py-1 text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                                            field.value === option.value
                                                ? "border-primary bg-primary font-bold text-white"
                                                : ""
                                        }`}
                                        onClick={() =>
                                            field.onChange(option.value)
                                        }
                                        role="radio"
                                        aria-checked={
                                            field.value === option.value
                                        }
                                        aria-label={`${option.label}を選択`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                            {(fieldState.isDirty || fieldState.isTouched) &&
                                fieldState.error?.message && (
                                    <div className="mb-0 ml-[14px] mr-[14px] mt-[3px] text-left font-sans text-xs font-normal leading-[1.66] tracking-[0.03333em] text-red-500">
                                        {fieldState.error.message}
                                    </div>
                                )}
                        </div>
                    )}
                />

                {/* 名前入力 */}
                {watchedRelationship && (
                    <>
                        <h1 className="mb-2 mt-8 text-left text-xl font-bold">
                            相手のお名前(フルネーム)
                            <br />
                            を教えてください！
                        </h1>
                        <div>
                            <TextLabel className="text-primary">
                                相手のお名前
                            </TextLabel>
                            <Controller
                                name="name"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <TextInput
                                        {...field}
                                        type="text"
                                        placeholder="ex) 田中 太郎"
                                        errorMessage={
                                            fieldState.isDirty ||
                                            fieldState.isTouched
                                                ? fieldState.error?.message
                                                : undefined
                                        }
                                        className="w-full"
                                    />
                                )}
                            />
                        </div>
                    </>
                )}

                {/* 通信手段選択 */}
                {watchedRelationship && watchedName.trim() && (
                    <>
                        <h1 className="mb-2 mt-8 text-left text-xl font-bold">
                            ご希望のコミュニケーション手段を選択してください。
                        </h1>
                        <p className="mb-3 text-sm text-gray-600">
                            親御さまの使い慣れたツールを選択してください。
                        </p>
                        <Controller
                            name="questionSendType"
                            control={control}
                            render={({ field }) => (
                                <div className="flex justify-center gap-8">
                                    <button
                                        type="button"
                                        className={`flex h-32 w-32 cursor-pointer items-center justify-center rounded-md border transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                                            field.value === "Email"
                                                ? "border-4 border-primary bg-primary font-bold text-white"
                                                : "border-gray-300 bg-white"
                                        }`}
                                        onClick={() => field.onChange("Email")}
                                        disabled={createNewAnswerer.isLoading}
                                        role="radio"
                                        aria-checked={field.value === "Email"}
                                        aria-label="メールでの通知を選択"
                                    >
                                        <img
                                            src={EmailImg}
                                            alt="メール"
                                            className={`mr-2 ${
                                                field.value === "Email"
                                                    ? "brightness-0 invert filter"
                                                    : ""
                                            }`}
                                        />
                                        <span>メール</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`flex h-32 w-32 cursor-pointer items-center justify-center rounded-md border transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                                            field.value === "Line"
                                                ? "border-4 border-primary bg-primary font-bold text-white"
                                                : "border-gray-300 bg-white"
                                        }`}
                                        onClick={() => {
                                            field.onChange("Line");
                                        }}
                                        disabled={createNewAnswerer.isLoading}
                                        role="radio"
                                        aria-checked={field.value === "Line"}
                                        aria-label="LINEでの通知を選択"
                                    >
                                        <img
                                            src={LineImg}
                                            alt="LINE"
                                            className="mr-2"
                                        />
                                        <span>LINE</span>
                                    </button>
                                </div>
                            )}
                        />
                    </>
                )}

                {/* Email選択時の入力 */}
                {watchedRelationship && watchedName.trim() && (
                    <div>
                        {watchedQuestionSendType === "Email" && (
                            <div className="mt-4">
                                <TextLabel className="text-primary">
                                    相手のメールアドレス
                                </TextLabel>
                                <Controller
                                    name="email"
                                    control={control}
                                    render={({ field, fieldState }) => {
                                        const shouldShowError =
                                            fieldState.isDirty ||
                                            fieldState.isTouched;
                                        const errorMessage = shouldShowError
                                            ? fieldState.error?.message
                                            : undefined;

                                        return (
                                            <TextInput
                                                {...field}
                                                type="text"
                                                placeholder="ex)xxxx@example.com"
                                                errorMessage={errorMessage}
                                                className="w-full"
                                            />
                                        );
                                    }}
                                />
                            </div>
                        )}
                        {/* 次へボタン / やり取り相手を登録するボタン */}
                        {watchedQuestionSendType && (
                            <div className="mt-8">
                                <Button
                                    type="submit"
                                    disabled={
                                        !isValid ||
                                        createNewAnswerer.isLoading ||
                                        createLineLink.isLoading
                                    }
                                    loading={
                                        createNewAnswerer.isLoading ||
                                        createLineLink.isLoading
                                    }
                                    className="mb-8 mt-2 h-12 w-full"
                                >
                                    {watchedQuestionSendType === "Line"
                                        ? "やり取り相手を登録する"
                                        : "次へ"}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </form>
        </div>
    );
};
